/**
 * Factory de hooks CRUD padronizados (TanStack Query) sobre um recurso REST
 * paginado do backend Flask. Reaproveitado por todos os cadastros.
 *
 *   const clientes = createResource<Cliente>('clientes', '/clientes')
 *   clientes.useList({ q }) · clientes.useCreate() · clientes.useUpdate() · clientes.useRemove()
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http } from './client'
import type { Paginated } from './types'

export type ListParams = Record<string, string | number | boolean | undefined>

/** Opções de query repassadas ao useQuery (ex.: staleTime longo p/ catálogos;
 *  placeholderData: keepPreviousData p/ manter a lista visível ao refiltrar;
 *  refetchInterval p/ telas operacionais com polling, como o Transporte Máquina). */
export type QueryOpts = { staleTime?: number; gcTime?: number; enabled?: boolean; placeholderData?: typeof keepPreviousData; refetchInterval?: number }

/** Remove chaves vazias/undefined antes de mandar como query string. */
function cleanParams(params: ListParams): ListParams {
  const out: ListParams = {}
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '' && v !== null) out[k] = v
  }
  return out
}

export function createResource<T extends { uuid: string }, Body = Partial<Record<string, unknown>>>(
  name: string,
  path: string,
  /** Parâmetros que vão em TODA chamada do recurso: lista, item, edição e exclusão.
   *  Existe para `todas_duplas=1` das telas fora da Frente e do Painel de Descarga
   *  (15/10/2026). O mesmo `name` do recurso sem eles faz uma gravação de um lado
   *  invalidar o cache do outro. */
  fixedParams: ListParams = {},
) {
  const params = cleanParams(fixedParams)
  const listKey = (p: ListParams = {}) => [name, 'list', p] as const
  const itemKey = (uuid: string) => [name, 'item', uuid, params] as const
  const config = Object.keys(params).length ? { params } : undefined

  function useList(listParams: ListParams = {}, options: QueryOpts = {}) {
    const clean = cleanParams({ ...params, ...listParams })
    return useQuery({
      queryKey: listKey(clean),
      // `signal` do React Query repassado ao axios: quando a queryKey muda (o mapa
      // navegou para outra área, o filtro mudou) a requisição anterior é ABORTADA
      // em vez de continuar ocupando conexão e chegar depois da atual.
      queryFn: ({ signal }) => http.get<Paginated<T>>(path, { params: clean, signal }),
      ...options,
    })
  }

  function useItem(uuid: string | null, options: QueryOpts = {}) {
    return useQuery({
      queryKey: itemKey(uuid ?? ''),
      queryFn: () => http.get<T>(`${path}/${uuid}`, config),
      enabled: Boolean(uuid),
      ...options,
    })
  }

  function useCreate() {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (body: Body) => http.post<T>(path, body),
      onSuccess: () => qc.invalidateQueries({ queryKey: [name] }),
    })
  }

  function useUpdate() {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: ({ uuid, body }: { uuid: string; body: Body }) => http.patch<T>(`${path}/${uuid}`, body, config),
      onSuccess: () => qc.invalidateQueries({ queryKey: [name] }),
    })
  }

  function useRemove() {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (uuid: string) => http.del<{ uuid: string; deleted: boolean }>(`${path}/${uuid}`, config),
      onSuccess: () => qc.invalidateQueries({ queryKey: [name] }),
    })
  }

  return { name, path, params, listKey, itemKey, useList, useItem, useCreate, useUpdate, useRemove }
}

export type Resource<T extends { uuid: string }, Body = Partial<Record<string, unknown>>> = ReturnType<
  typeof createResource<T, Body>
>
